// D8 create/move dialog (Andrei, 2026-10-25): when an agent is created in a department, given a
// seat, or moved between teams, the person making the change is asked about its memory reach in
// a simple dialog, not a configuration screen. Before the change is sent, `confirmReach` asks
// the server what every touched agent will read after it (`recall/reach-preview`, resolved by
// the Recall sync's own rule) and, when anyone's reach changes, shows it: what it will read, what
// it starts reading, what it stops reading. Confirm sends the change; Cancel sends nothing.
//
// Under the accepted rule the old department's access drops by itself. Keeping it would be a
// cross-department exception, which is undecided (D10), so it is a read-only line, not a choice.
// When memory is off (or the caller may not preview) nothing is asked: the change goes ahead.
import type { OperationInput, OperationOutput } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
} from "@companyos/ui";
import { type ReactNode, useCallback, useRef, useState } from "react";
import type { Api } from "../../api/client";
import { problemCode } from "../structure/parts";

export type ReachChange = OperationInput<"recall.reachPreview">["body"];
type Preview = OperationOutput<"recall.reachPreview">;
type AgentReach = Preview["agents"][number];
type Item = AgentReach["after"][number];

/** Nothing for anyone to be asked about: no agent named, none whose reach changes. */
const quiet = (p: Preview) =>
  p.agents.every((a) => a.membership_id !== null && a.gains.length === 0 && a.loses.length === 0);

type Pending = { preview: Preview | null; resolve: (ok: boolean) => void };

/**
 * `confirmReach(change)` resolves true when the change should be sent (confirmed, or nothing to
 * ask) and false when the person cancelled. Render `reachDialog` once in the calling component.
 */
export function useReachConfirm(api: Api, co: string) {
  const [pending, setPending] = useState<Pending | null>(null);
  const settled = useRef(false);
  const confirmReach = useCallback(
    async (change: ReachChange): Promise<boolean> => {
      let preview: Preview | null = null;
      try {
        preview = await api.client.recall.reachPreview({ params: { co }, body: change });
      } catch (e) {
        // memory off, not the caller's to preview, or a change the write itself refuses (409: a
        // DE's second seat, a sub-team without its parent): the change decides and says why
        const code = problemCode(e);
        if (
          code === "feature_disabled" ||
          code === "forbidden" ||
          code === "not_found" ||
          code === "conflict" ||
          code === "org_chart_cycle"
        )
          return true;
        preview = null; // shown as "could not be previewed"; the person still decides
      }
      if (preview && quiet(preview)) return true;
      settled.current = false;
      return new Promise<boolean>((resolve) => setPending({ preview, resolve }));
    },
    [api, co],
  );
  const close = (ok: boolean) => {
    if (settled.current) return;
    settled.current = true;
    pending?.resolve(ok);
    setPending(null);
  };
  const reachDialog = pending ? <ReachDialog preview={pending.preview} onClose={close} /> : null;
  return { confirmReach, reachDialog };
}

function ReachDialog({
  preview,
  onClose,
}: {
  preview: Preview | null;
  onClose: (ok: boolean) => void;
}) {
  const agents = preview?.agents.filter(
    (a) => a.membership_id === null || a.gains.length > 0 || a.loses.length > 0,
  );
  const one = agents?.length === 1 ? agents[0] : undefined;
  return (
    <AlertDialog open onOpenChange={(o) => (o ? null : onClose(false))}>
      <AlertDialogContent data-slot="reach-dialog" className="sm:max-w-[600px]">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {one ? `What ${one.name} will read in memory` : "What agents will read in memory"}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {agents
              ? "This change moves what memory these agents can read. Every agent reads the company level; a department's memory only with a seat in that department."
              : "What agents will read after this change could not be worked out just now. You can still go ahead."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {agents && (
          <div className="flex flex-col gap-5">
            {agents.map((a) => (
              <AgentSection key={a.membership_id ?? "new"} agent={a} titled={!one} />
            ))}
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => onClose(false)}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => onClose(true)}>Confirm</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const KIND: Record<Item["kind"], string> = {
  company: "company",
  space: "space",
  team: "team memory",
  seat: "its seat",
};

function ItemLine({ item }: { item: Item }) {
  return (
    <li className="flex flex-wrap items-baseline gap-x-2">
      <span>{item.name}</span>
      <span className="text-xs text-muted-foreground">{KIND[item.kind]}</span>
      {item.access === "read_write" && <Badge variant="neutral">reads and writes</Badge>}
    </li>
  );
}

/** The after-list grouped: the company level, its own seat, then each department. */
function groups(items: Item[]): [string, Item[]][] {
  const out = new Map<string, Item[]>();
  for (const i of items) {
    const g = i.kind === "seat" ? "Its own seat" : (i.department?.name ?? "Company level");
    out.set(g, [...(out.get(g) ?? []), i]);
  }
  return [...out.entries()].sort(([a], [b]) =>
    a === "Company level" ? -1 : b === "Company level" ? 1 : a.localeCompare(b),
  );
}

function AgentSection({ agent, titled }: { agent: AgentReach; titled: boolean }) {
  const dropped = [
    ...new Set(agent.loses.flatMap((i) => (i.department ? [i.department.name] : []))),
  ];
  return (
    <section
      className="flex flex-col gap-3"
      data-slot="reach-agent"
      aria-label={`Memory for ${agent.name}`}
    >
      {titled && <h3 className="font-medium">{agent.name}</h3>}
      <Block title="Will be able to read">
        {agent.after.length === 0 ? (
          <p className="text-muted-foreground">Nothing: it holds no seat.</p>
        ) : (
          groups(agent.after).map(([g, items]) => (
            <div key={g} className="flex flex-col gap-1">
              <p className="text-xs font-medium text-muted-foreground">{g}</p>
              <ul className="flex flex-col gap-0.5">
                {items.map((i) => (
                  <ItemLine key={`${i.kind}:${i.id ?? i.name}`} item={i} />
                ))}
              </ul>
            </div>
          ))
        )}
      </Block>
      {/* only when it read something before (else it would repeat the list above) */}
      {agent.before.length > 0 && agent.gains.length > 0 && (
        <Block title="Starts reading">
          <ul className="flex flex-col gap-0.5">
            {agent.gains.map((i) => (
              <ItemLine key={`${i.kind}:${i.id ?? i.name}`} item={i} />
            ))}
          </ul>
        </Block>
      )}
      {agent.loses.length > 0 && (
        <Block title="Will stop reading" tone="danger">
          <ul className="flex flex-col gap-0.5">
            {agent.loses.map((i) => (
              <ItemLine key={`${i.kind}:${i.id ?? i.name}`} item={i} />
            ))}
          </ul>
        </Block>
      )}
      {dropped.length > 0 && (
        <p className="text-xs text-muted-foreground" data-slot="reach-drop-note">
          Access to {dropped.join(" and ")} is removed automatically. Keeping it would be an
          exception across departments, which is not available yet.
        </p>
      )}
    </section>
  );
}

function Block({ title, tone, children }: { title: string; tone?: "danger"; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5">
      <p
        className={
          tone === "danger" ? "text-sm font-medium text-danger-text" : "text-sm font-medium"
        }
      >
        {title}
      </p>
      {children}
    </div>
  );
}
